<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div v-if="visible" class="modal-overlay" @click="handleClose">
        <div class="modal-container" @click.stop>
          <!-- 关闭按钮 -->
          <button class="close-btn" @click="handleClose">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>

          <!-- 弹窗标题 -->
          <div class="modal-header">
            <h2 class="modal-title">潮推手App 多平台下载</h2>
          </div>

          <!-- 弹窗内容 -->
          <div class="modal-body">
            <p class="modal-description">潮推手App现已上线，请选择您使用的平台进行下载。</p>

            <!-- 二维码区域：广告位 pcAppDownloadAd 物料 -->
            <div class="qrcode-section">
              <div v-for="item in qrcodeItems" :key="item.id" class="qrcode-card">
                <div class="qrcode-wrapper">
                  <img :src="item.url" :alt="item.desc || item.title" class="qrcode-img" />
                </div>
                <p class="qrcode-label">{{ item.desc || item.title }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { advertApi, ADVERT_MEDIA_TYPE_IMAGE, type AdvertMaterial } from '@/api/advert'

const APP_DOWNLOAD_SPACE_CODE = 'pcAppDownloadAd'

interface QrcodeItem {
  id: number
  title: string
  desc: string
  url: string
}

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

const visible = computed(() => props.modelValue)
const qrcodeItems = ref<QrcodeItem[]>([])

const parseJumpParam = (raw: AdvertMaterial['jumpParam']): Record<string, unknown> => {
  if (!raw) return {}
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
    } catch {
      return {}
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw
  return {}
}

const mapQrItems = (materials: AdvertMaterial[]): QrcodeItem[] => {
  const items: QrcodeItem[] = []
  for (const material of materials) {
    const mediaList = material.media || []
    const media =
      mediaList.find((item) => item.mediaType === ADVERT_MEDIA_TYPE_IMAGE) || mediaList[0]
    const url = media?.mediaUrl?.trim()
    if (!url) continue
    const jumpParam = parseJumpParam(material.jumpParam)
    items.push({
      id: material.materialId,
      title: String(jumpParam.title || material.materialName || ''),
      desc: String(jumpParam.desc || ''),
      url,
    })
  }
  return items
}

const loadAppDownloadQrs = async () => {
  if (qrcodeItems.value.length > 0) return
  try {
    const res = await advertApi.query(APP_DOWNLOAD_SPACE_CODE)
    if (res?.code === 0) {
      qrcodeItems.value = mapQrItems(res.data?.materials || [])
    }
  } catch (error) {
    console.error('[DownloadModal] 获取下载二维码失败:', error)
  }
}

watch(
  () => props.modelValue,
  (open) => {
    if (open) void loadAppDownloadQrs()
  }
)

const handleClose = () => {
  emit('update:modelValue', false)
}
</script>

<style lang="scss" scoped>
// 遮罩层
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: var(--spacing-xl);
}

// 弹窗容器
.modal-container {
  position: relative;
  max-width: 600px;
  width: 100%;
  background: linear-gradient(145deg, rgba(32, 27, 38, 0.95) 0%, rgba(23, 19, 27, 0.95) 100%);
  border-radius: var(--radius-xl);
  padding: var(--spacing-xxl);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(143, 80, 234, 0.2);
  backdrop-filter: blur(20px);
}

// 关闭按钮
.close-btn {
  position: absolute;
  top: var(--spacing-lg);
  right: var(--spacing-lg);
  width: 32px;
  height: 32px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: var(--text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: var(--text-primary);
    transform: rotate(90deg);
  }
}

// 弹窗头部
.modal-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);

  .modal-title {
    font-size: var(--font-xxl);
    font-weight: 700;
    background: var(--gradient-primary);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    margin-bottom: var(--spacing-sm);
  }
}

// 弹窗主体
.modal-body {
  .modal-description {
    font-size: var(--font-md);
    color: var(--text-secondary);
    line-height: 1.6;
    margin-bottom: var(--spacing-md);
    text-align: center;
  }
}

// 二维码区域
.qrcode-section {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
}

.qrcode-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);

  .qrcode-wrapper {
    width: 180px;
    height: 180px;
    padding: var(--spacing-md);
    background: var(--primary-dark);
    border-radius: var(--radius-md);
    border: 2px solid rgba(143, 80, 234, 0.2);
    transition: all 0.3s ease;

    &:hover {
      border-color: var(--primary-color);
      box-shadow: 0 8px 24px rgba(143, 80, 234, 0.3);
      transform: translateY(-4px);
    }
    .qrcode-img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  .qrcode-label {
    font-size: var(--font-md);
    color: var(--text-primary);
    font-weight: 500;
    text-align: center;
  }
}

// 过渡动画
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;

  .modal-container {
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;

  .modal-container {
    transform: scale(0.9) translateY(20px);
    opacity: 0;
  }
}

// 响应式设计
@media (max-width: 768px) {
  .modal-container {
    padding: var(--spacing-xl);
  }

  .qrcode-section {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }

  .qrcode-card {
    .qrcode-wrapper {
      width: 160px;
      height: 160px;
    }
  }

  .modal-header {
    .modal-title {
      font-size: var(--font-xl);
    }
  }
}
</style>
